import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Params, Router } from '@angular/router';
import { parseZone } from 'moment';

import { OrderDetailsShot } from '../../../models/common/order-details-shot.model';
import { OrderStatus } from '../../../models/common/order-status.models';
import { SidebarTypeEnum } from '../../../models/common/sidebar-type.enum';
import { CompanyOrderDetails } from '../../../models/company/company-order-details.model';
import { CompanyService } from '../../../services/company.service';
import { ShotStatusEnum } from '../../../shared/models/shot-status.enum';
import { TopbarService } from '../../../shared/services/topbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GraphQLService } from '../../../services/graph-ql.service'
import { AppService } from '../../../app.service';
import { GetQuery } from '../../../models/shared/enums/get-query.enum';
import { map } from 'rxjs';

@Component({
  standalone: false,
  selector: 'bt-company-order-info',
  templateUrl: './company-order-info.component.html',
  styleUrls: ['./company-order-info.component.scss']
})
export class CompanyOrderInfoComponent implements OnChanges {

  @Input() orderId: number;
  @Input() orderNumber: string;
  @Input() modelId: number;

  @Output() exit: EventEmitter<void> = new EventEmitter<void>();

  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #companyService = inject(CompanyService);
  readonly #topbarService = inject(TopbarService);
  readonly #graphQLService = inject(GraphQLService);
  readonly #appService = inject(AppService);

  orderDetails: { orderStatus?: OrderStatus, data: Array<CompanyOrderDetails>, dataToView: Array<CompanyOrderDetails>, selected?: CompanyOrderDetails, search: string, totalLength?: number, areFiltered?: boolean } = {
    data: [],
    dataToView: [],
    search: ''
  };

  currentSidebarType: SidebarTypeEnum;
  sidebarTypeEnum = SidebarTypeEnum;
  shotStatusEnum = ShotStatusEnum;
  disableLeftArrow = true;
  disableRightArrow = false;
  tmpShot: OrderDetailsShot;
  photoAction: 'approve' | 'reject';
  pubblicationDate: string;
  loaderId = 'orderDetailsLoaderId';
  sidebarLoaderId = 'sidebarLoaderId';
  actionComments: string;
  colorId: number;
  todayDate: string;
  disablePublishShots: boolean;

  constructor() {
    this.todayDate = parseZone(new Date()).format('yyyy-MM-DD');
    this.searchTextSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orderId) {
      this.orderId ? this.getCompanyOrderDetails() : this.orderDetails.data = [];
    }
  }

  public setStylesToView(fromSearch?: boolean) {
    const search = this.orderDetails.search ? this.orderDetails.search.toLowerCase() : '';
    this.orderDetails.dataToView = this.orderDetails.data.filter(x => x.fullName.toLowerCase().indexOf(search) >= 0);
    this.orderDetails.areFiltered = !!this.orderDetails.search && (this.orderDetails.areFiltered || fromSearch);
  }

  public onSelectStyle(item?: CompanyOrderDetails) {
    this.modelId = item ? item.modelId : 0;
    this.colorId = item ? item.colorId : 0;
    this.orderDetails.selected = item;
    this.setDisablePublish();
    const par: Params = item ? { orderId: this.orderId, orderNumber: this.orderNumber, modelId: item.modelId } : { orderId: this.orderId, orderNumber: this.orderNumber, };
    this.#router.navigate([], { queryParams: par });
  }

  public onStyleComments() {
    this.currentSidebarType = SidebarTypeEnum.shotsComments;
  }

  public onPhotoAction(shot: OrderDetailsShot, action: 'approve' | 'reject') {
    this.tmpShot = shot;
    this.photoAction = action;
    this.currentSidebarType = SidebarTypeEnum.customSidebar;
  }

  public onPublishShots() {
    this.pubblicationDate = '';
    this.currentSidebarType = SidebarTypeEnum.customSidebar;
  }

  public publishShots() {

  }

  public onShotAction() {
    let statusId: ShotStatusEnum;
    if (this.photoAction === 'approve') statusId = ShotStatusEnum.Approved;
    if (this.photoAction === 'reject') {
      statusId = ShotStatusEnum.Rejected;
      const text = 'Rejected shot';
      this.actionComments = this.actionComments?.trim() ? text + ': ' + this.actionComments : text;
    }
    this.#companyService.updateShotStatus(this.tmpShot.shotId, statusId!, this.actionComments, this.sidebarLoaderId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.onExitSidebar();
        this.getCompanyOrderDetails();
      });
  }

  public onBack() {
    this.exit.emit();
  }

  public onExitSidebar() {
    this.currentSidebarType = null;
    this.tmpShot = null;
    this.photoAction = null;
    this.actionComments = null;
  }

  public onOrderComments() {
    this.currentSidebarType = SidebarTypeEnum.orderComments;
  }

  private setDisablePublish() {
    this.disablePublishShots = !this.orderDetails.selected || this.orderDetails.selected.shotTypes.some(x => !x.shot);
  }

  private searchTextSubscription() {
    this.#topbarService.searchTextSubscribe
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(data => {
        if (data !== this.orderDetails.search) {
          this.orderDetails.search = data;
          this.setStylesToView(true);
        }
      });
  }

  private getCompanyOrderDetails() {
    this.#graphQLService
      .getData(GetQuery.GET_COMPANY_ORDER_DETAILS, { companyId: this.#appService.companyId, orderId: this.orderId })
      .pipe(map((res: any )=> res['data']['companyOrderDetails']['nodes'][0]), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (data) => {
          this.orderDetails.data = data.orderDetails;
          this.orderDetails.orderStatus = data.orderStatus;
          let index = -1;
          if (this.modelId) index = data.orderDetails.findIndex(x => x.modelId === this.modelId && (!this.colorId || x.colorId === this.colorId));
          this.setStylesToView();
          this.onSelectStyle(this.orderDetails.dataToView.length ? this.orderDetails.dataToView[index >= 0 ? index : 0] : undefined);
        }
      });
  }
}
